import { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import '../Analitos.scss';
import DataGrid, { Column, Button as Butt, Editing, RequiredRule, Form, Lookup, Toolbar, Item, SearchPanel, Popup as Pop, Paging, Pager } from 'devextreme-react/data-grid';
import { useApiConfiguration } from '../../../api/api.configuration';
import Button from 'devextreme-react/button';
import { Tooltip } from 'devextreme-react';
import { UnitMeasurement } from '../../../images/icons';
import { UnitMeasurementBase } from './UnitMeasurementBase';
import { useAppSelector } from '../../../contexts/hooks';
import { useScreenSize } from '../../../utils/media-query';
import { Item as Items } from 'devextreme-react/form';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import notify from 'devextreme/ui/notify';

export const UnitMeasurements = ({ dataUnidades, update }) => {
  const dataRef = useRef<DataGrid>(null);
  const { newUnitMeasurement, setUnitMeasurement, deleteUnitMeasurement, getUnitsBase } = useApiConfiguration();
  const [unitBase, setUnitBase] = useState();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { isMedium, isSmall, isXSmall } = useScreenSize();
  const Sedes = useAppSelector((x) => x.configuration.Data?.ComboBox?.find((y) => y.Name === 'SysI_Sedes'));

  const updateData = () => {
    getUnitsBase().then((res) => {
      setUnitBase(res);
    });
  };

  useEffect(() => {
    getUnitsBase().then((res) => {
      setUnitBase(res);
    });
  }, []);

  const handleModalVisible = useCallback(() => {
    setIsModalVisible(true);
  }, []);

  const customData = useMemo(() => {
    return new CustomStore({
      key: 'Um_Id',
      load() {
        return Promise.resolve({
          data: dataUnidades,
        }).catch(() => {
          throw new Error('Error de carga de datos');
        });
      },
      insert: async (values) => {
        const res = await newUnitMeasurement({ Unidad: values });
        if (res !== 105) throw new Error('Error insertando la unidad de medida');
        update(); // refetch: la fila nueva necesita el Id generado por el back
      },
      update: async (key, values) => {
        const res = await setUnitMeasurement({ Unidad: values, IdUnidad: key });
        if (res !== 107) throw new Error('Error actualizando la unidad de medida');
        update(); // refetch (igual que el botón Refrescar) → se ve el cambio sin clic
      },
      remove: async (key) => {
        const res = await deleteUnitMeasurement({ IdUnidad: key });
        if (res !== 107) throw new Error('Error eliminando la unidad de medida');
        // el grid quita la fila localmente, sin recargar
      },
    });
  }, [dataUnidades]);

  const customDataSrc = useMemo(
    () =>
      new DataSource({
        store: customData,
        reshapeOnPush: true,
      }),
    [customData]
  );

  const handleRefresh = useCallback(() => {
    update();
  }, [update]);

  const handleDuplicate = useCallback((e: any) => {
    const rowData = e.row.data;
    if (!rowData) return;

    // Agregar nueva fila al grid
    dataRef.current?.instance.addRow();

    // Después de un pequeño delay, establecer los valores duplicados
    setTimeout(() => {
      const visibleRows = dataRef.current?.instance.getVisibleRows();
      const newRowIndex = visibleRows?.findIndex((row: any) => row.isNewRow);

      if (newRowIndex !== -1 && newRowIndex !== undefined) {
        // Establecer valores duplicados (excepto el ID)
        dataRef.current?.instance.cellValue(newRowIndex, 'Um_Nombre', `${rowData.Um_Nombre} - Copia`);
        dataRef.current?.instance.cellValue(newRowIndex, 'Um_Sd_Id', rowData.Um_Sd_Id);
        dataRef.current?.instance.cellValue(newRowIndex, 'Um_Umb_Id1', rowData.Um_Umb_Id1);
        dataRef.current?.instance.cellValue(newRowIndex, 'Um_FactorConversion1', rowData.Um_FactorConversion1);
        dataRef.current?.instance.cellValue(newRowIndex, 'Um_Umb_Id2', rowData.Um_Umb_Id2);
        dataRef.current?.instance.cellValue(newRowIndex, 'Um_FactorConversion2', rowData.Um_FactorConversion2);
      }
    }, 100);

    notify('📋 Unidad de medida duplicada. Complete la edición para guardar.', 'info', 3000);
  }, []);

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <DataGrid dataSource={customDataSrc} ref={dataRef} width='100%' height='100%' columnAutoWidth={true} columnHidingEnabled={true} allowColumnResizing={true} showBorders={true} rowAlternationEnabled={true} hoverStateEnabled={true}>
        <SearchPanel visible={true} width={300} placeholder='Buscar unidades...' />
        <Paging enabled={true} defaultPageSize={25} />
        <Pager visible={true} allowedPageSizes={[25, 50, 100, 200, 'all']} displayMode='full' showPageSizeSelector={true} showInfo={true} showNavigationButtons={true} />
        <Editing mode={isSmall || isXSmall || isMedium ? 'popup' : 'form'} allowUpdating={true} allowAdding={true} allowDeleting={true} selectTextOnEditStart={true} texts={{ saveRowChanges: 'Guardar', cancelRowChanges: 'Cancelar', deleteRow: 'Eliminar', addRow: 'Agregar' }}>
          <Pop title='Unidad de medida' showTitle={true} fullScreen={isXSmall || isSmall} width={isMedium ? '70%' : 600} height='auto' />
          <Form colCount={isSmall || isXSmall ? 1 : 2} colCountByScreen={{ xs: 1, sm: 1, md: 2, lg: 2 }}>
            <Items dataField='Um_Nombre' colSpan={isSmall || isXSmall ? 1 : 2} />
            <Items dataField='Um_Sd_Id' />
            <Items dataField='Um_Umb_Id1' />
            <Items dataField='Um_FactorConversion1' />
            <Items dataField='Um_Umb_Id2' />
            <Items dataField='Um_FactorConversion2' />
          </Form>
        </Editing>
        <Toolbar>
          <Item name='searchPanel' />
          <Item location='before' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='UnidadMedidaBase' icon={UnitMeasurement} text='Unidad medida' stylingMode='text' visible={true} disabled={false} onClick={handleModalVisible} />
            <Tooltip target={'#UnidadMedidaBase'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Unidad medida base
            </Tooltip>
          </Item>
          <Item name='addRowButton' />
          <Item location='after' locateInMenu='auto' showText='inMenu' widget='dxButton'>
            <Button id='RefrecarResultun' icon='refresh' text='Refresh' stylingMode='text' onClick={handleRefresh} />
            <Tooltip target={'#RefrecarResultun'} showEvent='mouseenter' hideEvent='mouseleave' position={'top'}>
              Refrescar datos
            </Tooltip>
          </Item>
        </Toolbar>
        <Column allowGrouping={false} caption='ID' visible={true} allowEditing={false} dataField='Um_Id' alignment='center' width={80} sortOrder='desc' hidingPriority={0} />
        <Column allowGrouping={false} minWidth={100} caption='UNIDAD' dataField='Um_Nombre' alignment='center' visible={true} allowEditing={true} hidingPriority={6}>
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} minWidth={100} caption='SEDE' visible={true} allowEditing={true} dataField='Um_Sd_Id' alignment='center' hidingPriority={5}>
          <Lookup dataSource={Sedes?.Data} valueExpr='Sd_Id' displayExpr='Sd_Nombre' />
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} minWidth={110} caption='UNIDAD BASE 1' visible={true} allowEditing={true} dataField='Um_Umb_Id1' alignment='center' hidingPriority={4}>
          <Lookup dataSource={unitBase} valueExpr='Umb_Id' displayExpr='Umb_Nombre' />
          <RequiredRule />
        </Column>
        <Column allowGrouping={false} minWidth={110} caption='FACTOR CONVERSION 1' visible={true} allowEditing={true} dataField='Um_FactorConversion1' alignment='center' hidingPriority={3} />
        <Column allowGrouping={false} minWidth={110} caption='UNIDAD BASE 2' visible={true} allowEditing={true} dataField='Um_Umb_Id2' alignment='center' hidingPriority={2}>
          <Lookup dataSource={unitBase} valueExpr='Umb_Id' displayExpr='Umb_Nombre' />
        </Column>
        <Column allowGrouping={false} minWidth={110} caption='FACTOR CONVERSION 2' visible={true} allowEditing={true} dataField='Um_FactorConversion2' alignment='center' hidingPriority={1} />
        <Column type='buttons' caption={'ACCIÓN'} width={100}>
          <Butt name='edit' />
          <Butt name='delete' />
          <Butt icon='copy' hint='Duplicar unidad de medida' onClick={handleDuplicate} />
        </Column>
      </DataGrid>
      <UnitMeasurementBase visible={isModalVisible} setVisible={() => setIsModalVisible(false)} onUpdate={updateData} />
    </div>
  );
};
